import { useState, useEffect, useRef, useLayoutEffect, useMemo } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Link, useLocation } from 'react-router-dom';
import Text from "./text.jsx";
import Lottie from "lottie-react";
import quizAnimation from "../quiz.json";
import Button from "./button.jsx";

gsap.registerPlugin(ScrollTrigger);

const Intro = ({scrollProgress}) => {

    const [ book, setBook ] = useState("");
    const bookRef = useRef(null);

    const getBook = async () => {
        try {
            const response = await fetch("/logos/book.svg");
            if (response.ok) {
                const data = await response.text();
                setBook(data);
            } else {
                console.log("SVG logo wasn't loaded!");
            }

        } catch (err) {
            console.error(err);
        }
    };

    const [logo, setLogo] = useState("");
    
    const getLogo = async () => {
        try {
            const response = await fetch("/logos/logo.svg");
            if (response.ok) {
                const data = await response.text();
                setLogo(data);
            } else {
                console.log("SVG logo wasn't loaded!");
            }
        } catch (err) {
            console.error(err);
        }
    };

    const location = useLocation();
    
    useEffect(() => {
        getBook();
        getLogo();
    }, [location.pathname]);

    const memoryBook = useMemo(() => {
        if (!book) return null;
        return (
            <div
                className="book"
                ref={bookRef}
                dangerouslySetInnerHTML={{ __html: book }}
            />
        );
    }, [book]);

    const scrollRef = useRef(false);
    const swirlTl = useRef();

    const startSwirl = (letters) => {
        if (swirlTl.current) {
            swirlTl.current.kill();
        }

        const targetRadius = 120;
        
        const startPositions = Array.from(letters).map(letter => ({
            x: gsap.getProperty(letter, "x") || 0,
            y: gsap.getProperty(letter, "y") || 0
        }));

        const tracker = { 
            progress: 0, 
            transition: 0
        };

        swirlTl.current = gsap.timeline({ repeat: -1 });

        gsap.to(tracker, {
            transition: 1,
            duration: 2,
            ease: "power2.out"
        });

        swirlTl.current.to(tracker, {
            progress: 1,
            duration: 20,
            ease: "none",
            onUpdate: () => {
                letters.forEach((letter, i) => {
                    const offset = i / letters.length;
                    const angle = (offset + tracker.progress) * Math.PI * 2;

                    const swirlX = Math.cos(angle) * targetRadius;
                    const swirlY = Math.sin(angle) * targetRadius;

                    const finalX = startPositions[i].x + (swirlX - startPositions[i].x) * tracker.transition;
                    const finalY = startPositions[i].y + (swirlY - startPositions[i].y) * tracker.transition;

                    gsap.set(letter, {
                        x: finalX,
                        y: finalY,
                        rotation: tracker.progress * 360
                    });
                });
            }
        });
    };

    useLayoutEffect(() => {

        if (!bookRef.current) return;

        if (swirlTl.current) {
            swirlTl.current.kill();
        }

        const elem = bookRef.current.querySelector("#book");

        const letters = bookRef.current.querySelectorAll(
            "[id^='letter'], [id^='lamp']"
        );

        gsap.fromTo(elem, {
            opacity: 0,
            x: 400,
        }, {
            opacity: 1,
            x: 0,
            duration: 0.4
        });

        gsap.fromTo(letters, {
            opacity: 0,
            y: 100,
        }, {
            opacity: 1,
            y: 0,
            duration: 0.1,
            stagger: 0.05,
            delay: 0.4,
            onComplete: () => startSwirl(letters)
        });

    }, [book]);

    useEffect(() => {

        if (!bookRef.current) return;

        const letters = bookRef.current.querySelectorAll(
            "[id^='letter'], [id^='lamp']"
        );
        
        if (!letters.length) return;

        if (swirlTl.current) {
            swirlTl.current.kill(); 
        }

        letters.forEach((letter, index) => {

            if (scrollProgress > 0 && !scrollRef.current) {

                gsap.to(letter, {
                    opacity: 0,
                    y: 100,
                    duration: 0.1,
                    delay: index * 0.05,
                    onComplete: () => {
                        if (index === letters.length - 1) {
                            scrollRef.current = true;
                        }
                    },
                });
            }

            if(scrollProgress === 0 && scrollRef.current) {

                gsap.set(letter, { rotation: 0});

                gsap.to(letter, {
                    opacity: 1,
                    y: 0,
                    duration: 0.1,
                    delay: index * 0.05,
                    onComplete: () => {
                        if (index === letters.length - 1) {
                            scrollRef.current = false;
                            startSwirl(letters);
                        }
                    },
                });
            }

        });

    }, [scrollProgress]);

    const textRef = useRef();
    const logoRef = useRef();
    const buttonRef = useRef();

    useLayoutEffect(() => {
        if (!logoRef.current) return;

        const tl = gsap.timeline();
        if (logoRef.current) tl.to(logoRef.current, { opacity: 1, x: 0, duration: 0.3 }, 0);
        if (textRef.current) tl.to(textRef.current, { opacity: 1, y: 0, duration: 0.3 }, 0.3);
        if (buttonRef.current) tl.to(buttonRef.current, { opacity: 1, y: 0, duration: 0.3 }, 0.4);

    }, [logo]);

    useEffect(() => {
        const subjects = document.querySelectorAll(".subject");

        subjects.forEach((el, index) => {
            gsap.fromTo(el, 
                { 
                    x: -100, 
                    opacity: 0 
                }, 
                {
                    x: 0,
                    opacity: 1,
                    duration: 1,
                    delay: largeScreen ? index * 0.2 : 0,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: el,
                        start: largeScreen ? "top 90%" : "top 70%",
                        end: "top 50%",
                        scrub: false,
                        toggleActions: "play none none none",
                    }
                }
            );
        });

        return () => {
            ScrollTrigger.getAll().forEach(trigger => trigger.kill());
        };
    }, []);

    const [ letters, setLetters ] = useState(false);
    const [ largeScreen, setLargeScreen ] = useState(window.innerWidth > 1100);
    const [ desktopScreen, setDesktopScreen ] = useState(window.innerWidth >= 1400);

    useEffect(() => {

        const handleResize = () => {

            setLargeScreen(window.innerWidth > 1100);
            setDesktopScreen(window.innerWidth >= 1400);

        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };

    }, []);

    return (

        <div className='intro'>

            <div className="intro1">
                {memoryBook}

                <div className="welcome-text-container">

                {logo &&
                    <div 
                    className="flex-row"
                    ref={logoRef}
                    style={{
                        opacity: 0, 
                        transform: "translateX(-100%)",
                        gap: 0,
                        width: "min-content",
                        marginLeft: 20,
                        marginBottom: 20,
                    }}
                    >
                        <div
                            className="nav-logo"
                            dangerouslySetInnerHTML={{ __html: logo }}
                        />

                        <div>
                            <h1 className="title">RAMMATISCH</h1>
                        </div>
                    </div>
                }

                {logo &&
                    <div style={{marginLeft: 25, color: "var(--blue)", opacity: 0, transform: "translateY(-60px)", maxWidth: "80%"}} ref={textRef}>
                        <h2>Dein digitaler <span style={{color: "var(--yellow)"}}>Lernbegleiter</span> für den Deutschunterricht!</h2>
                    </div>
                }

            </div>
            </div>

            <div className="intro2">
                <h1 className="title title-small">SO FUNKTIONIERT'S</h1>

                <div className="box">

                    <h2>Thema wählen</h2>

                    <p>
                        Wähle gezielt grammatische Themen aus, die im Unterricht aktuell sind – von Wortarten über Satzbau bis zu Zeiten und Fällen. Die Inhalte orientieren sich an schulischen Lehrplänen oder lassen sich massgeschneidert auf den eigenen Unterricht anpassen.
                    </p>

                </div>

                <div className="box">

                    <h2>Übungen erstellen und lösen</h2>

                    <p>Mit<span style={{color: "var(--yellow)", fontWeight: 600}}> GRAMMATISCH</span> kannst du nicht nur bestehende Übungsaufgaben lösen, sondern auch eigene Übungen erstellen, die gezielt auf deinen Unterricht ausgerichtet sind. Und das ohne Mehraufwand, denn wir übernehmen die grammatische Aufbereitung und Analyse.</p>

                </div>

                <div className="box">

                    <h2>Fortschritte sehen</h2>

                    <p>
                        Behalte deinen Lernfortschritt im Blick. So lassen sich individuelle Stärken fördern und gezielte Unterstützungsangebote planen – im Unterricht wie auch zu Hause.
                    </p>

                </div>

            </div>

            <div className="intro3">

                <div style={{maxWidth: "1400px", padding: desktopScreen ? "50px" : "0px"}}>
                    <h1 className="title title-small" style={{color: "var(--white)"}}>THEMA WÄHLEN</h1>

                    <div className="subjects">

                        <Link to="/uebungen/verben" className="subject" onMouseEnter={() => setLetters("verbs")} onMouseLeave={() => setLetters(null)}>
                            <Text text="Verben" fontColor="var(--white)" textSize="1.5rem" weight={600} disappear={letters === "verbs"} />

                            <p>Zeiten, Konjugation und Verbposition im Satz sicher anwenden.</p>

                            <img src="/images/verbs.png" alt="image related to verbs" />
                        </Link>

                        <Link to="/uebungen/nomen" className="subject" onMouseEnter={() => setLetters("nouns")} onMouseLeave={() => setLetters(null)}>
                            <Text text="Nomen" fontColor="var(--white)" textSize="1.5rem" weight={600} disappear={letters === "nouns"} />

                            <p>Artikel und Kasus sicher bestimmen, Subjekt und Objekt unterscheiden.</p>

                            <img src="/images/nouns.png" alt="image related to nouns" />
                        </Link>

                        <div className="subject" onMouseEnter={() => setLetters("adjectives")} onMouseLeave={() => setLetters(null)}>
                            <Text text="Adjektive" fontColor="var(--white)" textSize="1.5rem" weight={600} disappear={letters === "adjectives"} />

                            <p>Adjektive richtig deklinieren und steigern und mit den zugehörigen Bezugswörtern verbinden.</p>

                            <img src="/images/syntax.png" alt="image related to adjectives" style={{height: '80%'}} />
                        </div>

                        <div className="subject" onMouseEnter={() => setLetters("syntax")} onMouseLeave={() => setLetters(null)}>
                            <Text text="Satzlehre" fontColor="var(--white)" textSize="1.5rem" weight={600} disappear={letters === "syntax"} />

                            <p>Satzbau sicher, korrekt und stilvoll anwenden.</p>

                            <img src="/images/adj.png" alt="image related to syntax" style={{height: '80%'}} />
                        </div>

                        <div className="subject" onMouseEnter={() => setLetters("other")} onMouseLeave={() => setLetters(null)}>
                            <Text text="Benutzerdefiniert" fontColor="var(--white)" textSize="1.5rem" weight={600} disappear={letters === "other"} />

                            <p>Erstele eigenen Übungen und bestimme selbst, auf welchen Themen der Fokus liegen soll.</p>

                            <img src="/images/flexible.png" alt="image related to syntax" style={{height: '80%'}} />
                        </div>

                    </div>
                </div>

            </div>

            <div className="intro4">

                <div className="quiz-text">
                    <h1 className="title title-small" style={{marginBottom: largeScreen ? 40 : ''}}>QUIZ MODUS</h1>

                    <div style={{marginTop: 10}}>
                        <p style={{marginLeft: 2}}>Teste dein Wissen gegen Freunde und Bekannte!</p>

                        <p style={{marginLeft: 2, marginTop: 10}}>In unserem Quizmodus kannst du deine Deutschkenntnisse auf spielerischem Wege testen und deine Fortschritte mit anderen Lernenden vergleichen.</p>
                    </div>

                    {largeScreen &&
                        <Link to="/quiz" style={{marginTop: 20, width: "max-content"}}>
                            <Button className="button-yellow" text="Zum Quiz" />
                        </Link>
                    }
                </div>

                <div className="quiz-image">
                    <Lottie 
                        animationData={quizAnimation} 
                        loop={true}
                    />
                </div>

                {!largeScreen &&
                    <Link to="/quiz" style={{margin: "auto", marginTop: 20, width: "max-content"}}>
                        <Button className="button-yellow" text="Zum Quiz" />
                    </Link>
                }

            </div>

        </div>

    );

}; export default Intro;